import { bindPanelCollapse, panelEnd, panelStart } from './collapsiblePanel';
import { RCT_CELL } from '../core/track/rctPieces';
import { sidebarPane } from './sidebarTabs';

const STORAGE_KEY = 'rcsim.snapGrid';

export interface SnapGridPrefs {
  enabled: boolean;
  size: number;
  /** Always show XYZ labels on every control point. */
  showCoords: boolean;
}

export interface GridPanelCallbacks {
  onChange(prefs: SnapGridPrefs): void;
}

function loadPrefs(): SnapGridPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { enabled: false, size: RCT_CELL, showCoords: false };
    const parsed = JSON.parse(raw) as Partial<SnapGridPrefs>;
    const size = typeof parsed.size === 'number' && isFinite(parsed.size) ? parsed.size : RCT_CELL;
    return {
      enabled: parsed.enabled === true,
      size: Math.max(0.25, Math.min(50, size)),
      showCoords: parsed.showCoords === true,
    };
  } catch {
    return { enabled: false, size: RCT_CELL, showCoords: false };
  }
}

function savePrefs(prefs: SnapGridPrefs): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

/** Snap-to-grid toggle, cell size, and coordinate labels — editor prefs (not in the coaster file). */
export class GridPanel {
  private el: HTMLElement;
  private prefs: SnapGridPrefs;

  constructor(private cb: GridPanelCallbacks) {
    this.prefs = loadPrefs();
    this.el = document.createElement('div');
    sidebarPane('build').appendChild(this.el);
    this.render();
    this.cb.onChange({ ...this.prefs });
  }

  getPrefs(): SnapGridPrefs {
    return { ...this.prefs };
  }

  private render(): void {
    const { enabled, size, showCoords } = this.prefs;
    this.el.innerHTML = `
      ${panelStart('grid', 'Grid')}
        <div class="row"><label><input type="checkbox" data-action="enabled" ${enabled ? 'checked' : ''}> Snap to grid</label></div>
        <div class="row"><label>Grid size (m)</label>
          <input type="number" data-action="size" min="0.25" max="50" step="0.25" value="${size}">
        </div>
        <div class="row"><label><input type="checkbox" data-action="showCoords" ${showCoords ? 'checked' : ''}> Show coordinates</label></div>
        <div class="hint">Snap locks dragged nodes to the grid (shown when on). Coordinates label every control point; while dragging, XYZ always appears.</div>
      ${panelEnd()}`;

    bindPanelCollapse(this.el);

    this.el.querySelector<HTMLInputElement>('input[data-action="enabled"]')?.addEventListener('change', (e) => {
      this.prefs.enabled = (e.target as HTMLInputElement).checked;
      savePrefs(this.prefs);
      this.cb.onChange({ ...this.prefs });
    });

    this.el.querySelector<HTMLInputElement>('input[data-action="size"]')?.addEventListener('change', (e) => {
      const v = parseFloat((e.target as HTMLInputElement).value);
      if (!isFinite(v)) return;
      this.prefs.size = Math.max(0.25, Math.min(50, v));
      (e.target as HTMLInputElement).value = String(this.prefs.size);
      savePrefs(this.prefs);
      this.cb.onChange({ ...this.prefs });
    });

    this.el.querySelector<HTMLInputElement>('input[data-action="showCoords"]')?.addEventListener('change', (e) => {
      this.prefs.showCoords = (e.target as HTMLInputElement).checked;
      savePrefs(this.prefs);
      this.cb.onChange({ ...this.prefs });
    });
  }
}
